import React, { useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { QRCodeSVG } from 'qrcode.react';
import { ArrowRight, Clock, Users, Award, Brain, Sparkles, CheckCircle, Star, Zap, Globe, ChevronDown } from 'lucide-react';
import { Button } from '../components/ui/button';
import SEO from '../components/SEO';

const APP_URL = window.location.origin;

export default function PromoPage() {
  const [lang, setLang] = useState('fr');
  const topRef = useRef(null);

  const t = lang === 'fr' ? {
    badge: '2 HEURES OFFERTES',
    headline: "Il est temps de comprendre l'IA",
    sub: "La formation IA la plus complète en français et en anglais. Pour tous les publics, de 12 à 99 ans.",
    cta: "Commencer gratuitement",
    cta2: "Voir les programmes",
    free: "GRATUIT",
    audiences_title: "Pour qui ?",
    audiences: [
      { icon: '🎓', title: 'Collégiens & Lycéens', desc: "Prenez de l'avance sur votre avenir. Comprenez l'IA avant tout le monde." },
      { icon: '👨‍💻', title: 'Étudiants & Jeunes actifs', desc: "Boostez votre CV avec des compétences IA certifiées." },
      { icon: '💼', title: "Chefs d'entreprise & Managers", desc: "Prenez les bonnes décisions stratégiques grâce à l'IA." },
      { icon: '👩‍🏫', title: 'Enseignants & Formateurs', desc: "Intégrez l'IA dans vos méthodes pédagogiques." },
      { icon: '🔄', title: 'En reconversion', desc: "Ouvrez-vous de nouvelles opportunités professionnelles." },
      { icon: '👴', title: 'Grands-parents & Seniors', desc: "Comprenez le monde de demain. L'IA expliquée simplement." },
    ],
    features_title: "Ce que vous allez apprendre",
    features: [
      "Comprendre ce qu'est l'IA (et ce qu'elle n'est pas)",
      "Utiliser ChatGPT, Claude, Midjourney comme un pro",
      "Automatiser vos tâches répétitives",
      "Protéger vos données et votre vie privée",
      "Créer du contenu avec l'IA (texte, images, vidéo)",
      "Prendre des décisions éclairées sur l'IA",
    ],
    included_title: "Inclus dans vos 2 heures gratuites",
    included: [
      "4 modules de formation interactifs",
      "1 quiz avec corrections détaillées",
      "Accès au lexique de 15+ outils IA",
      "Quiz adaptatifs à votre niveau",
      "Interface en français et anglais",
      "Aucune carte bancaire requise",
    ],
    stats: [
      { value: '4', label: 'Programmes' },
      { value: '34', label: 'Modules' },
      { value: '9', label: 'Quiz' },
      { value: '100%', label: 'Gratuit pour commencer' },
    ],
    social_proof: "Formez-vous à votre rythme, où et quand vous voulez",
    testimonial: '"J\'ai compris en 2h ce que personne n\'avait pu m\'expliquer sur l\'IA."',
    testimonial_author: '— Lucas M., Lycéen',
    scan_title: "Scannez pour commencer",
    scan_sub: "ou partagez ce lien",
    share_title: "Partagez cette offre",
    share_wa: "Partager sur WhatsApp",
    share_email: "Envoyer par email",
    share_copy: "Copier le lien",
    copied: "Copié !",
    footer: "IA Academy — Progressez. 30 min/jour. Résultats concrets.",
    footer_sub: "La plateforme de formation IA #1 en français",
    urgency: "Offre de lancement — Places limitées",
    switch_lang: "English version",
  } : {
    badge: '2 FREE HOURS',
    headline: "It's Time to Understand AI",
    sub: "The most complete AI training in French and English. For everyone, from age 12 to 99.",
    cta: "Start for free",
    cta2: "View programs",
    free: "FREE",
    audiences_title: "Who is it for?",
    audiences: [
      { icon: '🎓', title: 'Students & Teens', desc: "Get ahead of your future. Understand AI before everyone else." },
      { icon: '👨‍💻', title: 'Young professionals', desc: "Boost your resume with certified AI skills." },
      { icon: '💼', title: 'Business Leaders & Managers', desc: "Make the right strategic decisions with AI." },
      { icon: '👩‍🏫', title: 'Teachers & Trainers', desc: "Integrate AI into your teaching methods." },
      { icon: '🔄', title: 'Career changers', desc: "Open new professional opportunities." },
      { icon: '👴', title: 'Grandparents & Seniors', desc: "Understand tomorrow's world. AI explained simply." },
    ],
    features_title: "What you'll learn",
    features: [
      "Understand what AI is (and what it isn't)",
      "Use ChatGPT, Claude, Midjourney like a pro",
      "Automate your repetitive tasks",
      "Protect your data and privacy",
      "Create content with AI (text, images, video)",
      "Make informed decisions about AI",
    ],
    included_title: "Included in your 2 free hours",
    included: [
      "4 interactive training modules",
      "1 quiz with detailed corrections",
      "Access to 15+ AI tools lexicon",
      "Adaptive quizzes to your level",
      "Interface in French and English",
      "No credit card required",
    ],
    stats: [
      { value: '4', label: 'Programs' },
      { value: '34', label: 'Modules' },
      { value: '9', label: 'Quizzes' },
      { value: '100%', label: 'Free to start' },
    ],
    social_proof: "Learn at your own pace, anytime, anywhere",
    testimonial: '"I understood in 2 hours what no one had been able to explain to me about AI."',
    testimonial_author: '— Lucas M., High School Student',
    scan_title: "Scan to start",
    scan_sub: "or share this link",
    share_title: "Share this offer",
    share_wa: "Share on WhatsApp",
    share_email: "Send by email",
    share_copy: "Copy link",
    copied: "Copied!",
    footer: "IA Academy — Level Up. 30 Min a Day. Real Results.",
    footer_sub: "The #1 AI training platform in French",
    urgency: "Launch offer — Limited spots",
    switch_lang: "Version française",
  };

  const promoUrl = `${APP_URL}/promo`;
  const registerUrl = `${APP_URL}/auth?mode=register`;
  const [copied, setCopied] = useState(false);

  const waMsg = lang === 'fr'
    ? `🚀 *Il est temps de comprendre l'IA !*\n\n🎁 2 heures de formation GRATUITES\n👨‍👩‍👧‍👦 Pour TOUT public (collégiens, pros, seniors...)\n🏆 Certificats + Quiz adaptatifs\n\n👉 ${promoUrl}`
    : `🚀 *It's time to understand AI!*\n\n🎁 2 FREE hours of training\n👨‍👩‍👧‍👦 For EVERYONE (students, pros, seniors...)\n🏆 Certificates + Adaptive quizzes\n\n👉 ${promoUrl}`;

  const emailSubject = lang === 'fr'
    ? 'Formation IA gratuite — 2 heures offertes | IA Academy'
    : 'Free AI Training — 2 free hours | IA Academy';

  const emailBody = lang === 'fr'
    ? `Bonjour,\n\nJe voulais vous partager cette formation IA gratuite.\n\n🎁 2 heures offertes\n📱 Accessible à tous (de 12 à 99 ans)\n🏆 Certificats reconnus\n\nInscrivez-vous ici : ${promoUrl}\n\nBonne formation !\nIA Academy`
    : `Hello,\n\nI wanted to share this free AI training with you.\n\n🎁 2 free hours\n📱 Accessible to everyone (from 12 to 99)\n🏆 Recognized certificates\n\nSign up here: ${promoUrl}\n\nHappy learning!\nIA Academy`;

  const copyLink = () => {
    navigator.clipboard.writeText(promoUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="min-h-screen bg-[#0B1120]" ref={topRef} data-testid="promo-page">
      <SEO
        title={lang === 'fr' ? 'Offre Promo - 2h de Formation IA Gratuites' : 'Promo - 2h Free AI Training'}
        description={lang === 'fr'
          ? "Profitez de 2 heures de formation IA gratuites avec IA Academy. Maîtrisez ChatGPT, Claude et les outils IA essentiels. Offre limitée !"
          : "Get 2 hours of free AI training with IA Academy. Master ChatGPT, Claude and essential AI tools. Limited offer!"}
        path="/promo"
      />
      {/* Floating language switch */}
      <button
        onClick={() => setLang(l => l === 'fr' ? 'en' : 'fr')}
        className="fixed top-4 right-4 z-50 flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/10 backdrop-blur border border-white/20 text-white text-xs font-medium hover:bg-white/20 transition-colors"
        data-testid="promo-lang-switch"
      >
        <Globe className="w-3.5 h-3.5" /> {t.switch_lang}
      </button>

      {/* Hero Section */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-blue-600/20 via-transparent to-transparent" />
        <div className="absolute top-20 left-10 w-72 h-72 bg-blue-500/10 rounded-full blur-3xl" />
        <div className="absolute top-40 right-20 w-96 h-96 bg-orange-500/10 rounded-full blur-3xl" />
        
        <div className="max-w-5xl mx-auto px-4 pt-12 pb-16 relative z-10 text-center">
          {/* Logo */}
          <div className="flex items-center justify-center gap-3 mb-8">
            <img src="https://customer-assets.emergentagent.com/job_2071b760-1de1-4ab9-81d8-8ca2ec59ffcc/artifacts/lq8u7cy5_LOGO%20IA.png" alt="IA Academy" className="w-14 h-14 rounded-xl" />
            <div className="text-left">
              <h2 className="text-2xl font-bold text-white" style={{ fontFamily: 'Poppins' }}>IA Academy</h2>
              <p className="text-xs text-blue-300 tracking-wider">{lang === 'fr' ? 'Progressez. 30 min/jour. Résultats concrets.' : 'Level Up. 30 Min a Day. Real Results.'}</p>
            </div>
          </div>

          {/* Urgency badge */}
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-orange-500/20 border border-orange-500/40 text-orange-300 text-sm font-medium mb-6 animate-pulse" data-testid="promo-urgency">
            <Zap className="w-4 h-4" /> {t.urgency}
          </div>

          {/* Free badge */}
          <div className="inline-flex items-center gap-2 px-6 py-2 rounded-full bg-emerald-500 text-white text-lg font-bold mb-6 ml-3 shadow-lg shadow-emerald-500/30" data-testid="promo-free-badge">
            <Sparkles className="w-5 h-5" /> {t.badge}
          </div>

          {/* Headline */}
          <h1 className="text-4xl sm:text-5xl lg:text-7xl font-bold text-white mb-6 leading-tight" style={{ fontFamily: 'Poppins' }} data-testid="promo-headline">
            {t.headline}
          </h1>

          <p className="text-lg sm:text-xl text-slate-300 max-w-2xl mx-auto mb-10" data-testid="promo-subtitle">
            {t.sub}
          </p>

          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-12">
            <Link to="/auth?mode=register">
              <Button className="bg-orange-500 hover:bg-orange-600 text-white text-lg px-8 py-6 rounded-xl font-bold shadow-lg shadow-orange-500/30 w-full sm:w-auto" data-testid="promo-cta-main">
                {t.cta} <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
            <Link to="/programs">
              <Button variant="outline" className="border-white/20 text-white hover:bg-white/10 text-lg px-8 py-6 rounded-xl w-full sm:w-auto" data-testid="promo-cta-programs">
                {t.cta2}
              </Button>
            </Link>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 max-w-2xl mx-auto" data-testid="promo-stats">
            {t.stats.map((s, i) => (
              <div key={i} className="bg-white/5 backdrop-blur border border-white/10 rounded-xl p-4">
                <p className="text-2xl font-bold text-white" style={{ fontFamily: 'Poppins' }}>{s.value}</p>
                <p className="text-xs text-slate-400">{s.label}</p>
              </div>
            ))}
          </div>

          <ChevronDown className="w-6 h-6 text-white/30 mx-auto mt-10 animate-bounce" />
        </div>
      </section>

      {/* Audiences Section */}
      <section className="py-16 bg-[#0F172A]">
        <div className="max-w-5xl mx-auto px-4">
          <h2 className="text-3xl font-bold text-white text-center mb-3" style={{ fontFamily: 'Poppins' }} data-testid="promo-audiences-title">
            {t.audiences_title}
          </h2>
          <p className="text-slate-400 text-center mb-10 text-sm">{t.social_proof}</p>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="promo-audiences-grid">
            {t.audiences.map((a, i) => (
              <div key={i} className="bg-white/5 border border-white/10 rounded-xl p-5 hover:bg-white/10 transition-colors" data-testid={`promo-audience-${i}`}>
                <span className="text-3xl mb-3 block">{a.icon}</span>
                <h3 className="text-white font-semibold mb-1">{a.title}</h3>
                <p className="text-slate-400 text-sm">{a.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features + Included */}
      <section className="py-16 bg-[#0B1120]">
        <div className="max-w-5xl mx-auto px-4">
          <div className="grid md:grid-cols-2 gap-8">
            {/* What you'll learn */}
            <div className="bg-gradient-to-br from-blue-600/20 to-blue-900/20 border border-blue-500/20 rounded-2xl p-8" data-testid="promo-features">
              <Brain className="w-8 h-8 text-blue-400 mb-4" />
              <h3 className="text-xl font-bold text-white mb-5" style={{ fontFamily: 'Poppins' }}>{t.features_title}</h3>
              <ul className="space-y-3">
                {t.features.map((f, i) => (
                  <li key={i} className="flex items-start gap-3 text-slate-300">
                    <CheckCircle className="w-5 h-5 text-blue-400 mt-0.5 flex-shrink-0" />
                    <span>{f}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Included */}
            <div className="bg-gradient-to-br from-emerald-600/20 to-emerald-900/20 border border-emerald-500/20 rounded-2xl p-8" data-testid="promo-included">
              <Award className="w-8 h-8 text-emerald-400 mb-4" />
              <h3 className="text-xl font-bold text-white mb-5" style={{ fontFamily: 'Poppins' }}>{t.included_title}</h3>
              <ul className="space-y-3">
                {t.included.map((f, i) => (
                  <li key={i} className="flex items-start gap-3 text-slate-300">
                    <CheckCircle className="w-5 h-5 text-emerald-400 mt-0.5 flex-shrink-0" />
                    <span>{f}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonial */}
      <section className="py-12 bg-[#0F172A]">
        <div className="max-w-3xl mx-auto px-4 text-center">
          <div className="flex justify-center gap-1 mb-4">
            {[...Array(5)].map((_, i) => <Star key={i} className="w-5 h-5 text-amber-400 fill-amber-400" />)}
          </div>
          <blockquote className="text-xl sm:text-2xl text-white italic mb-4" data-testid="promo-testimonial">
            {t.testimonial}
          </blockquote>
          <p className="text-slate-400">{t.testimonial_author}</p>
        </div>
      </section>

      {/* QR Code + Share Section */}
      <section className="py-16 bg-[#0B1120]" id="share">
        <div className="max-w-4xl mx-auto px-4">
          <div className="bg-gradient-to-br from-slate-800 to-slate-900 border border-slate-700 rounded-2xl p-8 md:p-12">
            <div className="grid md:grid-cols-2 gap-10 items-center">
              {/* QR Code */}
              <div className="text-center" data-testid="promo-qr-section">
                <h3 className="text-2xl font-bold text-white mb-2" style={{ fontFamily: 'Poppins' }}>{t.scan_title}</h3>
                <p className="text-slate-400 text-sm mb-6">{t.scan_sub}</p>
                <div className="inline-block bg-white p-4 rounded-2xl shadow-2xl shadow-blue-500/10 mb-4" data-testid="promo-qr-code">
                  <QRCodeSVG
                    value={registerUrl}
                    size={200}
                    level="H"
                    includeMargin={false}
                    fgColor="#0B1120"
                    imageSettings={{
                      src: "https://customer-assets.emergentagent.com/job_2071b760-1de1-4ab9-81d8-8ca2ec59ffcc/artifacts/lq8u7cy5_LOGO%20IA.png",
                      height: 40,
                      width: 40,
                      excavate: true,
                    }}
                  />
                </div>
                <p className="text-xs text-slate-500 break-all select-all font-mono bg-slate-800 rounded-lg px-3 py-2" data-testid="promo-url">
                  {promoUrl}
                </p>
              </div>

              {/* Share options */}
              <div data-testid="promo-share-section">
                <h3 className="text-2xl font-bold text-white mb-6" style={{ fontFamily: 'Poppins' }}>{t.share_title}</h3>
                <div className="space-y-3">
                  <a
                    href={`https://wa.me/?text=${encodeURIComponent(waMsg)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-3 w-full px-5 py-3.5 rounded-xl bg-[#25D366] text-white font-medium hover:bg-[#20BD5A] transition-colors"
                    data-testid="promo-share-wa"
                  >
                    <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
                    {t.share_wa}
                  </a>

                  <a
                    href={`mailto:?subject=${encodeURIComponent(emailSubject)}&body=${encodeURIComponent(emailBody)}`}
                    className="flex items-center gap-3 w-full px-5 py-3.5 rounded-xl bg-blue-600 text-white font-medium hover:bg-blue-700 transition-colors"
                    data-testid="promo-share-email"
                  >
                    <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/></svg>
                    {t.share_email}
                  </a>

                  <button
                    onClick={copyLink}
                    className="flex items-center gap-3 w-full px-5 py-3.5 rounded-xl bg-slate-700 text-white font-medium hover:bg-slate-600 transition-colors"
                    data-testid="promo-share-copy"
                  >
                    <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="9" y="9" width="13" height="13" rx="2" ry="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/></svg>
                    {copied ? t.copied : t.share_copy}
                  </button>
                </div>

                {/* Final CTA */}
                <Link to="/auth?mode=register">
                  <Button className="w-full mt-6 bg-orange-500 hover:bg-orange-600 text-white text-lg py-6 rounded-xl font-bold shadow-lg shadow-orange-500/30" data-testid="promo-final-cta">
                    {t.cta} <ArrowRight className="w-5 h-5 ml-2" />
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 bg-[#0B1120] border-t border-slate-800">
        <div className="max-w-5xl mx-auto px-4 text-center">
          <div className="flex items-center justify-center gap-2 mb-2">
            <img src="https://customer-assets.emergentagent.com/job_2071b760-1de1-4ab9-81d8-8ca2ec59ffcc/artifacts/lq8u7cy5_LOGO%20IA.png" alt="IA Academy" className="w-8 h-8 rounded-lg" />
            <span className="text-white font-bold" style={{ fontFamily: 'Poppins' }}>{t.footer}</span>
          </div>
          <p className="text-slate-500 text-sm">{t.footer_sub}</p>
          <p className="text-slate-600 text-xs mt-4">&copy; 2026 IA Academy. {lang === 'fr' ? 'Tous droits réservés.' : 'All rights reserved.'}</p>
        </div>
      </footer>
    </div>
  );
}
